import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, Bell, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (ms) => {
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
  return h >= 1 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${String(s).padStart(2, "0")}s`;
};

export const NextDropTeaser = () => {
  const [drop, setDrop] = useState(null);
  const [now, setNow] = useState(Date.now());
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const key = drop ? `cmb_notify_${drop.public_at}` : "";
  useEffect(() => { if (key) setDone(localStorage.getItem(key) === "1"); }, [key]);

  const subscribe = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/drops/notify", { email: user ? "" : email });
      localStorage.setItem(key, "1"); setDone(true); setOpen(false);
      toast.success(`We'll email ${data.email} the moment it goes public`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't sign you up"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    api.get("/drops/next").then((r) => setDrop(r.data)).catch(() => {});
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!drop) return null;
  const ms = new Date(drop.public_at).getTime() - now;
  if (ms <= 0) return null;

  return (
    <section className="px-6 md:px-12 pt-6 relative z-10">
      <div data-testid="next-drop-teaser"
        className="relative overflow-hidden rounded-2xl border border-[#e2ff31]/30 bg-[#0f0f0f] px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="absolute inset-y-0 left-0 w-1 bg-[#e2ff31]" />
        <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#e2ff31]">
          <span className="relative flex w-2 h-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#e2ff31] opacity-60" /><span className="relative w-2 h-2 rounded-full bg-[#e2ff31]" /></span>
          New drop incoming
        </span>
        <p className="font-heading text-lg sm:text-xl font-semibold">
          Next {drop.genre ? <span className="text-[#e2ff31]">{drop.genre}</span> : null} drop in{" "}
          <span data-testid="next-drop-countdown" className="font-mono tabular-nums">{fmt(ms)}</span>
        </p>
        <p className="text-sm text-[#9ca3af] flex items-center gap-1.5 lg:ml-auto">
          <Lock className="w-3.5 h-3.5 shrink-0" /> Past buyers are hearing it first.
        </p>
        {done ? (
          <span data-testid="notify-done" className="inline-flex items-center gap-1.5 text-sm text-[#22c55e] font-semibold"><Check className="w-4 h-4" /> You're on the list</span>
        ) : open && !user ? (
          <form onSubmit={subscribe} className="flex gap-2 w-full sm:w-auto">
            <input data-testid="notify-email-input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" className="flex-1 sm:w-56 rounded-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#e2ff31] outline-none px-4 py-2 text-sm" />
            <button data-testid="notify-submit" disabled={busy} className="rounded-full bg-[#e2ff31] text-black font-semibold text-sm px-4 py-2 hover:bg-[#c8e619] transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Notify me"}
            </button>
          </form>
        ) : (
          <button data-testid="notify-me-btn" onClick={() => (user ? subscribe() : setOpen(true))} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#e2ff31] text-black font-semibold text-sm px-4 py-2 hover:bg-[#c8e619] active:scale-95 transition-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Notify me
          </button>
        )}
      </div>
    </section>
  );
};
